import { IBaseResponse } from "@/@types/ResponseModel";
import config from "@/config";
import axiosApi from "@/core/interceptor/interceptor";
import { DefaultRowsPerPage } from "@/utils/constant/constant";

export interface PaymentCard {
  id: string;
  payment_id: string;
  card_details: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  billing_details: {
    name: string;
    email: string;
  };
  is_default: boolean;
  created_at: string;
}

export const UserService = {
  getUserDetails: (): Promise<IBaseResponse<any>> => {
    return axiosApi.get(`${config.apiUrl}user/me`);
  },
  resendVerificationEmail: (body: { email: string, restaurant_id: string }): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}auth/resend-verification-email`, body);
  },
  forgotPassword: (body: { email: string, restaurant_id: string }): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}auth/forgot-password`, body);
  },
  verifyResetPasswordToken: (token: string, restaurant_id: string): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}auth/verify-reset-password-token`, { token, restaurant_id });
  },
  resetPassword: (token: string, restaurant_id: string, password: string): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}auth/reset-password`, { token, restaurant_id, password });
  },
  getSubMembers: (limit: number = DefaultRowsPerPage, page: number = 1): Promise<IBaseResponse<any>> => {
    return axiosApi.get(`${config.apiUrl}user/sub-members?limit=${limit}&page=${page}`);
  },
  updateSubMember: (id: number, member: any): Promise<IBaseResponse<any>> => {
    return axiosApi.put(`${config.apiUrl}user/sub-member/${id}`, member);
  },
  createSubMember: (member: any): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}user/sub-member`, member);
  },
  deleteSubMember: (id: number): Promise<IBaseResponse<any>> => {
    return axiosApi.delete(`${config.apiUrl}user/sub-member/${id}`);
  },
  updateProfile: (member: any): Promise<IBaseResponse<any>> => {
    return axiosApi.put(`${config.apiUrl}user/profile`, member);
  },
  updatePassword: (member: any): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}user/reset-password`, member);
  },
  getOrders: (limit: number = DefaultRowsPerPage, page: number = 1, dateFrom: string = '', dateTo: string = '', status: string = ''): Promise<IBaseResponse<any>> => {
    return axiosApi.get(`${config.apiUrl}user/orders?limit=${limit}&page=${page}${dateFrom && dateTo ? `&dateFrom=${dateFrom}&dateTo=${dateTo}` : ''}${status ? `&order_status=${status}` : ''}`);
  },
  getTransactions: (limit: number = DefaultRowsPerPage, page: number = 1, status: string = ''): Promise<IBaseResponse<any>> => {
    return axiosApi.get(`${config.apiUrl}user/transactions?limit=${limit}&page=${page}${status ? `&status=${status}` : ''}`);
  },
  downloadFiles: (files: string[]): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}user/download-files`, { files });
  },
  getPaymentMethods: (): Promise<IBaseResponse<PaymentCard[]>> => {
    return axiosApi.get(`${config.apiUrl}user/payment-methods`).then((res) => res.data);
  },
  getSetupIntent: (): Promise<IBaseResponse<any>> => {
    return axiosApi.get(`${config.apiUrl}user/setup-intent`).then((res) => res.data);
  },
  addPaymentMethod: (paymentMethod: any): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}user/payment-method`, paymentMethod);
  },
  setDefaultCard: (cardId: string): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}user/set-default-card`, { card_id: cardId });
  },
  deletePaymentMethod: (paymentId: string): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}user/delete-payment-method`, { payment_id: paymentId });
  },
  getOutstandingDues: (): Promise<IBaseResponse<any>> => {
    return axiosApi.get(`${config.apiUrl}user/outstanding-dues`);
  },
  processPayment: ({ source, paymentMethodId, methodId, paymentMethod, checkIds }: { source: string, paymentMethodId?: string, methodId?: number, paymentMethod?: any, checkIds?: number[] }): Promise<IBaseResponse<any>> => {
    return axiosApi.post(`${config.apiUrl}user/process-pending-payment`, { source, paymentMethodId, methodId, paymentMethod, checkIds });
  },
  verifySignupToken: (token: string, restaurant_id: number): Promise<IBaseResponse<{data: {isVerified: boolean, email: string | null}}>> => {
    return axiosApi.post(`${config.apiUrl}auth/verify-signup`, { token, restaurant_id });
  },
};